import { generatePlayerIframeUrlForEmbedInstance } from '../utils/playerUrl';
import { Logger } from '../logging/Logger';
import Bowser from 'bowser';
import BambuserLiveShopping from '../embed';
import { getDefaultPlayerSettings, parsePlayerSettings, parsePlayerSettingValue } from './settings';
import {
  INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT,
  PRODUCT_HYDRATION_TIMEOUT_MS,
  applyHydratedPreviewProducts,
  getPreviewProductsToHydrate,
  getRemainingProductsToHydrate,
  getRenderablePreviewProducts,
  hasProductId,
  parseProductData,
  removeProductHydrationCallback,
  shouldShowOnlyProductThumbnails,
} from './productCards';
import {
  buildCatalogRawSearchParams,
  catalogProductsToRawMap,
  mergeCatalogRawIntoProducts,
} from './catalogRaw';
import {
  ACTION_TYPE,
  BAM_PLAYER_FIT,
  CLOSED_CAPTIONS_MODE,
  HORIZONTAL_POSITION,
  PRODUCT_ACTION_TYPE,
  SCALE_MODE,
  FOCUS_MODE,
  NAVIGATION_MODE
} from './constants';
import { getStyleSheetForBamPlayer } from './bamPlayerStyles';
import {
  getAppearanceCssVars,
  getSafeAppearanceConfig,
  CONDITIONAL_APPEARANCE_CSS_PROPS
} from './appearanceCssVars';
import { resizedImage, DEFAULT_RESIZE_OPTIONS_PRODUCT_SMALL } from '../../../player/src/shared/utils/image';
import {
  formatDiscountPercentage,
  formatDiscountPercentageAriaLabel,
  resolveDiscountPercentage,
} from '../../../player/src/shared/utils/discountPercentage';
import { formatCapacity } from '../../../player/src/shared/utils/capacity';
import { toPublicProductData } from '../../../player/src/shared/utils/publicProduct';
import {
  Expand as ExpandIcon,
  OpenLink as OpenLinkIcon,
  TikTok as TikTokIcon,
  Instagram as InstagramIcon,
} from './svg';

// Source platforms whose VOD assets get an attribution badge in the
// preview overlay (logo + optional @handle + "Sponsored" disclosure).
const SOCIAL_MEDIA_PLATFORM_ICONS = {
  tiktok: TikTokIcon,
  instagram: InstagramIcon,
};
import { PLAYER_EVENT_PREFIX, PLAYER_EVENTS, PLAYER_EVENTS_PUBLIC } from '../constants';
import PriorityQueue from '../utils/priorityQueue';
import Deferred from '../utils/deferred';
import PostMessageConduit from '../PostMessageConduit';

const { setTimeout, clearTimeout, Intl } = window;

const MAX_FONT_SIZE = 20;
const MIN_FONT_SIZE = 8;

const HTML_ENTITIES = {
  '&': '&amp;',
  '"': '&quot;',
  "'": '&#39;',
  '<': '&lt;',
  '>': '&gt;',
};

const escapeHtml = (value) => String(value).replace(/[&"'<>]/g, character => (
  HTML_ENTITIES[character]
));

const toSafeCssValue = (value, fallback) => (
  typeof value === 'string' && value && !/[;{}\\]/.test(value) ? value : fallback
);

const browser = Bowser.getParser(window.navigator.userAgent);
const isUnsupportedBrowser = browser.satisfies({
  windows: { 'internet explorer': '<=11' },
  mobile: { safari: '<11' },
});

const validateUrl = (url) => {
  try {
    return new URL(url).toString();
  } catch (err) {
    return null;
  }
};

const formatPrice = (priceValue, currency, locale, shouldTrimPriceTrailingZeros) => {
  const minimumFractionDigits = shouldTrimPriceTrailingZeros ? 0 : undefined;
  const maximumFractionDigits = minimumFractionDigits;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(priceValue);
};

/**
 * A web component that embeds a Bambuser Live Shopping player.
 *
 * Example:
 *  <bam-player event-id="1234" autoplay="hover"></bam-player>
 *
 * The web component wraps a Bambuser Live Shopping player in an iframe,
 * loads it in a light-weight manner and provides a simple API to control
 * the player, including events and methods for play, pause, mute, etc.
 *
 * By default, the web component will expand the player when clicked.
 * This behavior can be disabled by setting the `standalone` attribute to `false`.
 *
 * Certain visual aspects of the player can be controlled via CSS custom
 * properties (see _updateStylesheet()).
 *
 * When initially attached to the DOM, the web component will load the player
 * in an iframe but only the video, not the player UI. The player UI will be
 * loaded when the user interacts with the player, e.g. by clicking on it.
 */
export default class BambuserPlayerComponent extends window.HTMLElement {
  // Changes in these attributes will trigger attributeChangedCallback()
  static observedAttributes = [
    'event-id',
    'video-id',
    'autoplay',
    'loop',
    'poster',
    'settings',
    'show-products',
    'defer-load',
  ];

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this._id = Math.random().toString(36).substring(2, 9);

    this._settings = getDefaultPlayerSettings();
    this._isProductViewShown = false;
    this._isProductListShown = false;
    this._isExpandedInPlaylist = false;
    this._poster = null;
    this._posterLowResBlob = null;
    this._isMuted = undefined;
    this._theme = {};
    this._showOnlyProductThumbnails = undefined;
    this._currentCaption = '';
    this._isDesktop = browser.getPlatformType(true) === 'desktop';
    this._playerReady = new Deferred();
    this._productHydrationState = null;
    this._lastRenderablePreviewProducts = null;
    this._productsDataRequestId = 0;
  }

  set _expanded(value) {
    this._isExpandedInPlaylist = value;

    if (this._isExpandedInPlaylist) {
      this.wrapper?.classList.add('is-expanded');
    } else {
      this.wrapper?.classList.remove('is-expanded');
    }
  }

  /** Standard Web Component methods */

  connectedCallback() {
    if (isUnsupportedBrowser) {
      // Render nothing if the browser is unsupported
      return;
    }
    if (this._hasConnected) return; // TODO: figure out why connectedCallback is called multiple times
    this._hasConnected = true;

    this._updateSettingsFromAttributes();
    this._setupDOM();
    this._setup();

    // Update the styles when the component is connected to the DOM for the first time to ensure
    // the container gets the correct dimensions immediately even if we end up deferring actual
    // setup.
    this._updateStylesheet();
  }

  disconnectedCallback() {
    this._cleanupProductHydration();
    this._advanceProductsDataRequestId();
    this._lastRenderablePreviewProducts = null;

    if (window.__bamPlayerResizeObserver) {
      window.__bamPlayerResizeObserver.unobserve(this.wrapper);
    }
    if (window.__bamPlayerIntersectionObserver) {
      window.__bamPlayerIntersectionObserver.unobserve(this.wrapper);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this._hasConnected) {
      // Do nothing if the component hasn't been connected to the DOM yet
      return;
    }

    if (this._isPlayerUILoaded) {
      // Do nothing if the player is expanded
      return;
    }

    if (newValue === oldValue) return;

    if (name === 'event-id' || name === 'video-id') {
      if (!newValue) {
        // Tear down the player if the source video attribute is removed
        this._tearDown();
        return;
      } else {
        this._tearDown();
        this._setupDOM();
        this._setup();
      }
    }

    if (name === 'autoplay') {
      // The autoplay attribute was changed. We need to re-setup the DOM event listeners.
      this.pause();
      this._unloadDOMEventListeners?.();
      this._setupDOMEventListeners();
    }

    if (name === 'poster') {
      this._updateStylesheet();
    }

    if (name === 'settings') {
      this._updateSettingsFromAttributes();
      this._updateOverlayDOM();
      this._updateStylesheet();
    }

    if (name === 'defer-load') {
      if (newValue !== 'true') {
        this._setup();
      }
    }
  }

  /** Custom methods */

  get _attr() {
    const eventId = this.getAttribute('event-id');
    const videoId = this.getAttribute('video-id');
    const autoplay = this.getAttribute('autoplay')?.toLocaleLowerCase() || this.hasAttribute('autoplay') || false;
    const loop = this.getAttribute('loop') || this.hasAttribute('loop') || false;
    const standalone = this.getAttribute('standalone') !== 'false';
    const poster = this.getAttribute('poster');
    const settings = this.getAttribute('settings');
    const focusMode = this.getAttribute('focus-mode');
    const navigationMode = this.getAttribute('navigation-mode') || NAVIGATION_MODE.NONE;

    // Do not try to parse the show-products attribute if it's not set
    const showProducts = this.getAttribute('show-products') === null
      ? undefined
      : parsePlayerSettingValue('products', this.getAttribute('show-products'));

    const showActions = this.getAttribute('show-actions') === null
      ? undefined
      : parsePlayerSettingValue('actions', this.getAttribute('show-actions'));

    const distributionPageId = this.getAttribute('distribution-page-id');
    const distributionContainerId = this.getAttribute('distribution-container-id');
    const playerFit = this.getAttribute('player-fit') || BAM_PLAYER_FIT.SAME_HEIGHT;
    const landscapeInPortrait =
      this.getAttribute('landscape-in-portrait') || SCALE_MODE.FIT;
    const showPosterWhenPaused = this.getAttribute('show-poster-when-paused') === 'true';
    const startMuted = this.getAttribute('start-muted') === 'true';
    const cookieDomain = this.getAttribute('cookie-domain');
    const cookieTTLDays = this.getAttribute('cookie-ttl-days');
    const collectMetrics = this.getAttribute('collect-metrics') !== 'false';
    const deferLoad = this.getAttribute('defer-load') === 'true';
    const productAction = this.getAttribute('product-action') || PRODUCT_ACTION_TYPE.EXPAND_PRODUCT;
    const disableExpandOnClick = this.getAttribute('disable-expand-on-click') === 'true';
    const closedCaptions = this.getAttribute('closed-captions') || CLOSED_CAPTIONS_MODE.OFF;
    const closedCaptionsLocaleHint = this.getAttribute('closed-captions-locale-hint') || null;

    const playlistId = this.getAttribute('playlist-id');
    const appearanceGroupId = this.getAttribute('appearance-group-id');
    const appearanceId = this.getAttribute('appearance-id');
    const placementId = this.getAttribute('placement-id');
    const themeId = this.getAttribute('theme-id');

    return {
      eventId,
      videoId,
      autoplay,
      loop,
      standalone,
      poster,
      settings,
      showActions,
      showProducts,
      distributionPageId,
      distributionContainerId,
      playerFit,
      landscapeInPortrait,
      showPosterWhenPaused,
      startMuted,
      collectMetrics,
      cookieDomain,
      cookieTTLDays,
      deferLoad,
      productAction,
      disableExpandOnClick,
      closedCaptions,
      closedCaptionsLocaleHint,
      playlistId,
      appearanceGroupId,
      appearanceId,
      placementId,
      themeId,
      focusMode,
      navigationMode,
    };
  }

  get _vodTrackingPayload() {
    const {
      distributionPageId,
      distributionContainerId,
      playlistId,
      appearanceGroupId,
      appearanceId,
      placementId,
    } = this._attr;

    return {
      distributionPageId,
      distributionContainerId,
      playlistId,
      appearanceGroupId,
      appearanceId,
      placementId,
    };
  }

  get _vodTrackingPayloadWithMetadata() {
    const {
      _inPlaylistIndex = null,
      _playlistLength = null,
      _playerConfig = {},
      _placementMetadata = {},
    } = this;

    return {
      ...this._vodTrackingPayload,
      playlistMetadata: {
        positionInPlaylist: _inPlaylistIndex,
        videosInPlaylist: _playlistLength,
      },
      appearanceMetadata: {
        ..._playerConfig,
      },
      placementMetadata: {
        ..._placementMetadata,
      }
    };
  }

  _getOverridableConfig() {
    return {};
  }

  get _withMinimizeSupport() {
    return [NAVIGATION_MODE.FAB, NAVIGATION_MODE.FAB_MANUAL].includes(this._attr?.navigationMode);
  }

  _getPlayerUrl({ forcePreload = false } = {}) {
    const {
      eventId,
      videoId,
      autoplay,
      standalone,
      distributionPageId,
      distributionContainerId,
      landscapeInPortrait,
      startMuted,
      closedCaptions,
      closedCaptionsLocaleHint,
      placementId,
      playlistId,
      appearanceGroupId,
      appearanceId,
      themeId,
    } = this._attr;

    // Tell the player to autoplay if set to any value other than "hover" or "visible" which are
    // options that we'll handle ourselves in the web component
    const shouldPlayerAutoplay = autoplay === true || (autoplay && autoplay !== 'hover' && autoplay !== 'visible');

    // Combine the config (optionally) provided by the parent page with our own necessary options
    const config = {
      ...this._getPlayerIntegrationConfig(),
      ...this.embedInstance?.config,
      eventId, // Either eventId or videoId is required, but not both
      videoId,
      autoplay: shouldPlayerAutoplay,
      type: 'overlay',
      playerVersion: 2,
      webComponentTagName: this.tagName?.toLowerCase(),
      withBaaSPlayerPreloading: true, // BaaS player should be preloaded outside of React app
      deferReactAppInit: true, // we will control when the React app (the player UI) should be loaded
      withMinimizeSupport: this._withMinimizeSupport,
      ...(!standalone && {
        // when we aren't handling everything inside bam-player, the controller is expected to add a close button
        hideCloseButton: true,
      }),
      ...(videoId && {
        hideShareButton: true,
        hideEmojiOverlay: true,
      }),
      ...(distributionPageId && { distributionPageId }),
      ...(distributionContainerId && { distributionContainerId }),
      ...(placementId && { placementId }),
      ...(playlistId && { playlistId }),
      ...(appearanceGroupId && { appearanceGroupId }),
      ...(appearanceId && { appearanceId }),
      ...(landscapeInPortrait && { landscapeInPortrait } ),
      ...(startMuted && { startMuted }),
      ...(standalone && { isVODStandalone: true }),
      ...(closedCaptions && { closedCaptions }),
      ...(closedCaptionsLocaleHint && { closedCaptionsLocaleHint }),
      ...(themeId && { themeId }),
      // Placement appearance toggles resolved by /distribution land on
      // `this._playerConfig` (set by bam-playlist). Forward them so the
      // iframe URL carries the placement's override; playerUrl.js skips
      // them when undefined so the iframe's default-visible kicks in.
      ...(this._playerConfig?.showSocialMediaHandle !== undefined
        && { showSocialMediaHandle: this._playerConfig.showSocialMediaHandle }),
      ...(this._playerConfig?.showSponsoredLabel !== undefined
        && { showSponsoredLabel: this._playerConfig.showSponsoredLabel }),
      ...this._getOverridableConfig(),
    };

    const url = generatePlayerIframeUrlForEmbedInstance({
      config,
      logger: new Logger(),
    })
      .replace(/\?/, '#'); // Pass the query params as hash params instead of query params

    // Append some query params of our own that aren't known by generatePlayerIframeUrlForEmbedInstance()
    const additionalQueryParams = new URLSearchParams({
      widgetId: this._id,
      useTopBarRightMargin: !this._isDesktop,
      ...(!forcePreload && {
        // Don't preload the video immediately if the player is not visible on
        // the page or not horizontally visible in the container
        noPreloadVideo: !this._isVisibleOnPage() || !this._isHorizVisibleInContainer(),
      }),
    });

    return `${url}&${additionalQueryParams.toString()}`;
  }

  _getPlayerIntegrationConfig() {
    // Return the configuration the merchant's integration code will
    // supply via player.configure() in onBambuserLiveShoppingReady()
    return this.embedInstance?.getProviderConfig() || {};
  }

  _updateSettingsFromAttributes() {
    const { settings: settingsAttributeValue } = this._attr;
    if (!settingsAttributeValue) return;

    this._settings = { ...this._settings, ...parsePlayerSettings(settingsAttributeValue)};

    // Reflect the parsed showScrollbars setting onto the host so the
    // shadow-DOM CSS (:host([show-scrollbars="true"])) matches.
    if (this._settings.showScrollbars) {
      this.setAttribute('show-scrollbars', 'true');
    } else {
      this.removeAttribute('show-scrollbars');
    }

    if (this.expandBtn) {
      this.expandBtn.dataset.visibility = this._settings.expandButtonVisibility;
    }
  };

  _updateStylesheet() {
    if (this.__updateStylesheetAF) return;
    this.__updateStylesheetAF = true;
    window.requestAnimationFrame(() => {
      this.__updateStylesheetAF = false;
      this.__updateStylesheet();
    });
  }

  __updateStylesheet() {
    let width = this._videoWidth;
    let height = this._videoHeight;
    if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
      width = null;
      height = null;
    }
    const aspectRatio = width && height ? width / height : undefined;

    // Calculate an appropriate font size based on the height of the container
    const { height: containerHeight, width: containerWidth } = this.getBoundingClientRect();
    const fontSize = Math.max(MIN_FONT_SIZE, Math.min(Math.round(containerHeight / 35), MAX_FONT_SIZE));

    const showOnlyThumbnails = shouldShowOnlyProductThumbnails({
      productCardMode: this._settings.productCardMode,
      containerWidth,
      containerHeight,
    });
    const shouldRefreshOverlayForProductMode =
      this._showOnlyProductThumbnails !== undefined &&
      this._showOnlyProductThumbnails !== showOnlyThumbnails &&
      this._overlayContent;
    this._showOnlyProductThumbnails = showOnlyThumbnails;

    // Use poster image if provided, either via the "poster" attribute or via the "pending curtain" on the show
    const { playerFit, poster: customPoster, showPosterWhenPaused } = this._attr;
    const { backgroundImage: pendingCurtainImage } = this?._event?.customPendingStyle || {};
    const posterRawUrl = validateUrl(customPoster) || validateUrl(this._poster) || validateUrl(pendingCurtainImage);
    const poster = posterRawUrl && containerHeight && containerWidth // Only render poster if we have dimensions
      ? resizedImage(posterRawUrl, {
        quality: 70,
        height: Math.ceil(containerHeight / 100) * 100, // Round to nearest 100 for better caching
      })
      : null;

    this.styleSheet.textContent = getStyleSheetForBamPlayer({
      aspectRatio,
      fontSize,
      showOnlyThumbnails,
      poster,
      posterLowResBlob: this._posterLowResBlob,
      playerFit,
      showPosterWhenPaused,
      theme: this._theme,
      isInlinePlayer: !!this._inlinePlayer,
    });

    if (shouldRefreshOverlayForProductMode) this._updateOverlayDOM();
  }

  // For social-media-sourced broadcasts (TikTok, Instagram), surface the
  // creator handle on the badge whenever the asset carries an author —
  // attribution is the goal of the badge. The title-prefix strip
  // handles the case where both the badge and the title row end up
  // visible (e.g. titles config on AND showSocialMediaHandle off) so
  // the @user isn't read twice. Returns null for non-social broadcasts
  // or when there's no broadcast metadata yet.
  _getSocialMediaAttributionParts() {
    if (!SOCIAL_MEDIA_PLATFORM_ICONS[this._sourcePlatform]) return null;
    const author = this._author;
    const permalink = this._permalink;
    if (!author && !permalink) return null;

    let handle = null;
    let trimmedTitle = (this._event && this._event.title) || '';

    if (author) {
      const bareAuthor = String(author).replace(/^@/, '');
      handle = `@${bareAuthor}`;
      const escaped = bareAuthor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const prefixRegex = new RegExp(`^@?${escaped}\\b[\\s\\-–—:|·•,~.;]*`, 'i');
      const prefixMatch = trimmedTitle.match(prefixRegex);
      if (prefixMatch) {
        const stripped = trimmedTitle.slice(prefixMatch[0].length).trim();
        // Hide the title if trimming leaves only a single character.
        trimmedTitle = stripped.length > 1 ? stripped : '';
      }
    }

    return { handle, permalink, trimmedTitle };
  }

  _getSocialMediaAttribution() {
    const parts = this._getSocialMediaAttributionParts();
    if (!parts) return '';
    const { handle, permalink } = parts;
    const logo = SOCIAL_MEDIA_PLATFORM_ICONS[this._sourcePlatform];

    // Appearance toggles — drive the same render decisions from the
    // placement's playerConfig (`showSocialMediaHandle`,
    // `showSponsoredLabel`). Both default to visible: only an explicit
    // `false` from the resolved config hides them. "Sponsored" is
    // additionally gated on `hasUgcRights` (a vod-derived flag — true
    // when the asset carries termsAgreement or importData.importType,
    // both set by paid / approved UGC ingest) — merchant's own posts
    // skip the disclosure.
    const showHandle = this._playerConfig?.showSocialMediaHandle !== false;
    const showSponsored = this._playerConfig?.showSponsoredLabel !== false
      && this._hasUgcRights;

    const handleHtml = (handle && showHandle)
      ? `<span class="social-media-attribution-handle">${handle}</span>`
      : '';
    const sponsoredHtml = showSponsored
      ? '<span class="social-media-sponsored">Sponsored</span>'
      : '';
    // Two stacked rows: logo + handle on top (logo alone when the handle
    // line is hidden), "Sponsored" on its own row beneath when enabled.
    const inner = `<span class="social-media-attribution-row">${logo}${handleHtml}</span>${sponsoredHtml}`;
    // Per-platform modifier class so per-logo sizing rules can win without
    // a specificity arms-race against the generic `svg` rule.
    const classNames = `social-media-attribution social-media-attribution-${this._sourcePlatform}`;
    // Only render as a link if the permalink is an http(s) URL — defensive
    // guard so an unexpected javascript: URL from social-media metadata
    // can't become a click target on the customer's page.
    const isSafeUrl = typeof permalink === 'string' && /^https?:\/\//i.test(permalink);
    if (!isSafeUrl) {
      return `<span class="${classNames}">${inner}</span>`;
    }
    return `<a class="${classNames}" href="${permalink}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
  }

  _getBasicInfoAsText() {
    const socialMediaParts = this._getSocialMediaAttributionParts();
    const title = socialMediaParts && socialMediaParts.handle
      ? socialMediaParts.trimmedTitle
      : ((this._event && this._event.title) || '');

    if (!title) return '';

    const { title: shouldDisplayTitle, overlayTextWrap: shouldWrapText } = this._settings;
    if (!shouldDisplayTitle) return '';

    return `
      <div class="basic-info ${shouldWrapText ? 'wrap-text' : ''}">
        <h1 class="title">${title}</h1>
      </div>
    `;
  }

  _getProductHtmlString(productData) {
    if (!productData || !Object.keys(productData).length) return '';

    const { _settings } = this;

    const playerIntegrationConfig = this._getPlayerIntegrationConfig();
    const showScrapedProductPrices = this._theme?.generalV2?.playerSettings?.misc?.showScrapedProductPrices ?? true;
    const { locale, trimPriceTrailingZeros } = playerIntegrationConfig;

    const processedProductData = showScrapedProductPrices ? productData : { ...productData, price: null };
    const {
      id,
      title: originalTitle,
      brand: originalBrand,
      thumbnail,
      price: parsedPrice,
      capacity,
    } = parseProductData(processedProductData, playerIntegrationConfig);

    if (!id) return '';

    const { brandAboveTitle = false, hideProductBrand = false } = this._theme?.generalV2?.playerSettings?.misc || {};
    let title, subtitle;
    if (hideProductBrand) {
      title = originalTitle;
      subtitle = '';
    } else if (brandAboveTitle) {
      title = originalBrand;
      subtitle = originalTitle;
    } else {
      title = originalTitle;
      subtitle = originalBrand;
    }
    const hidePricing = this._theme?.generalV2?.playerSettings?.highlight?.hidePricing ?? false;
    const price = hidePricing ? null : parsedPrice;

    const parsedCurrency = price?.currency ?? '';

    const priceStr = (() => {
      if (!price?.current) return '';
      const parsedPrice = parseFloat(price?.current);

      try {
        return formatPrice(parsedPrice, parsedCurrency, locale, trimPriceTrailingZeros);
      } catch (err) {
        return `${parsedPrice} ${parsedCurrency}`;
      }
    })();

    const originalPriceStr = (() => {
      if (!price?.original) return null;
      const parsedOriginalPrice = parseFloat(price?.original);

      try {
        return formatPrice(parsedOriginalPrice, parsedCurrency, locale, trimPriceTrailingZeros);
      } catch (err) {
        return `${parsedOriginalPrice} ${parsedCurrency}`;
      }
    })();

    const pricePerUnitStr = (() => {
      if (!price || typeof price !== 'object' || !price?.perUnit) return '';
      const parsedPricePerUnit = price?.perUnit;

      try {
        const formattedPrice = formatPrice(parsedPricePerUnit, parsedCurrency, locale, trimPriceTrailingZeros);
        return `${formattedPrice} / ${price?.unitAmount}${price?.unitDisplayName}`;
      } catch (err) {
        return `${parsedPricePerUnit} ${parsedCurrency} / ${price?.unitAmount}${price?.unitDisplayName}`;
      }
    })();
    const capacityStr = formatCapacity(capacity, locale) || '';
    const displayedPricePerUnit = capacityStr && pricePerUnitStr ? `(${pricePerUnitStr})` : pricePerUnitStr;
    const secondaryPriceStr = [capacityStr, displayedPricePerUnit].filter(Boolean).join(' ');
    const secondaryPriceHtml = secondaryPriceStr
      ? `<div class="product-price-per-unit">${escapeHtml(secondaryPriceStr)}</div>`
      : '';

    const showDiscount = price?.original && price?.current && price?.current < price?.original;
    const discountPercentageBadgeSettings = this._theme?.generalV2?.playerSettings?.discountPercentageBadge || {};
    const discountPercentage = resolveDiscountPercentage({ price, settings: discountPercentageBadgeSettings });
    const discountPercentageAriaLabel = formatDiscountPercentageAriaLabel({
      value: discountPercentage,
      label: this._theme?.generic?.discountPercentageAriaLabel,
      locale,
    });
    const discountPercentageBadgeStyle = [
      ['color', toSafeCssValue(discountPercentageBadgeSettings.foregroundColor, '#fff')],
      ['background-color', toSafeCssValue(discountPercentageBadgeSettings.backgroundColor, '#000')],
      ['border-radius', toSafeCssValue(discountPercentageBadgeSettings.borderRadius, '9999px')],
      ['padding', toSafeCssValue(discountPercentageBadgeSettings.padding, '2px 8px')],
    ].map(([property, value]) => `${property}:${value}`).join(';');
    const discountPercentageBadgeHtml = discountPercentage === null
      ? ''
      : `<div aria-label="${escapeHtml(discountPercentageAriaLabel)}" `
        + `class="product-discount-percentage-badge" style="${escapeHtml(discountPercentageBadgeStyle)}">`
        + `${escapeHtml(formatDiscountPercentage({
          value: discountPercentage,
          format: discountPercentageBadgeSettings.format,
          locale,
          withLabelTemplate: this._theme?.generic?.discountPercentageWithLabelTemplate,
        }))}</div>`;

    // Use imageTransformer to resize and cache the thumbnail
    const resizedThumbnailURL = thumbnail  && resizedImage(thumbnail, {
      ...DEFAULT_RESIZE_OPTIONS_PRODUCT_SMALL,

      // Since it's difficult to determine an organization's settings from the web component
      // we'll use the static IP proxy unconditionally.
      staticIP: 1,
    });

    const ariaTitle = [title, subtitle, priceStr, capacityStr, discountPercentageAriaLabel]
      .filter(Boolean)
      .join(' ');

    this.wrapper.setAttribute('aria-label', ariaTitle);

    const { aspectRatio, size } = this._theme?.generalV2?.playerSettings?.productImage || {};
    const thumbnailClasses = ['product-thumbnail-wrapper'];
    if (aspectRatio && aspectRatio !== 'default') {
      // We're using 2/3 in the player, but here the slashes don't seem to be working in class names
      // so renaming to "-by-"
      thumbnailClasses.push(`aspect-ratio-${aspectRatio.replace('/', '-by-')}`);
    }

    if (size) {
      thumbnailClasses.push(size);
    }

    const thumbnailHtml = `<div class="${thumbnailClasses.join(' ')}">
        <img src="${resizedThumbnailURL}" alt="${title}" onerror="this.parentElement.classList.add('img-error')" />
      </div>`;
    let productInfoHtml = `<div class="product-info">
        <div class="product-title"><span>${title}</span></div>
        ${subtitle ? `<div class="product-subtitle">${subtitle}</div>` : ''}
        ${(priceStr || secondaryPriceStr) ? `<div class="product-price-wrapper ${showDiscount ? 'discounted' : ''}">
          ${priceStr ? `<div class="product-price">${priceStr}</div>` : ''}
          ${showDiscount ? `<div class="product-original-price">${originalPriceStr}</div>` : ''}
          ${discountPercentageBadgeHtml}
          ${secondaryPriceHtml}
        </div>` : '' }
      </div>`;

    switch (_settings.productCardMode) {
      case 'thumbnail':
        productInfoHtml = '';
        break;
      case 'full':
      case 'default':
      default:
        // Do nothing
        // In 'full' we always show both thumbnail and product info
        // In 'default' we show both if there's enough space, just thumbnail otherwise
        break;
    }

    return '<li>'
      + `<div class="product" id="${id}" role="button" aria-label="${escapeHtml(ariaTitle)}" tabindex="0">
      ${thumbnailHtml}
      ${productInfoHtml}
    </div></li>`;
  }

  _getProductsInfo() {
    const { _products, _settings } = this;
    const { showProducts } = this._attr;
    const products = getRenderablePreviewProducts({
      products: _products,
      productsToHydrate: this._productHydrationState?.productsToHydrate,
      fallbackProducts: this._lastRenderablePreviewProducts,
    });

    if (showProducts === 0 || _settings.products === 0) return '';
    if (!products.length) {
      this._lastRenderablePreviewProducts = null;
      return '';
    }

    const productsToShow = showProducts === undefined ? _settings.products : showProducts;

    const productsHtmlString = products.slice(0, productsToShow).map(product => {
      return this._getProductHtmlString(product);
    }).join('');

    if (!productsHtmlString) {
      this._lastRenderablePreviewProducts = null;
      return '';
    }

    this._lastRenderablePreviewProducts = products;

    return `
      <ul class="products">
          ${productsHtmlString}
      </ul>
    `;
  }

  _getActionCardInfo() {
    const { actions: shouldDisplayActions } = this._settings;
    const { showActions } = this._attr;

    if (!(shouldDisplayActions ?? showActions)) return '';

    const { _actions } = this;

    if (!_actions || !_actions.length) return '';

    /* eslint-disable */
    return `
      <div class="actions">
        ${_actions?.map(({ id, type, data }) => {
        if (type === ACTION_TYPE.LINK) {
          const text = data?.text;
          const url = data?.url;
          const actionCardIconUrl = this._theme.generalV2?.playerSettings?.actionCards?.icon;
          const icon = actionCardIconUrl ? `<img src="${actionCardIconUrl}" />` : OpenLinkIcon;
          return `
            <div title="${url}" class="action" id="${id}" role="button" tabindex="0">
              <div class="action-title">${text}</div>
              <div class="action-icon">${icon}</div>
            </div>
          `;
          }
        }
        ).join('')}
    `;
    /* eslint-enable */
  }

  _updateOverlayDOM() {
    const { overlay } = this;

    const closedCaptions = this._createClosedCaptions();
    // Current business rule is to show product or if no product, show basic info (title)
    const productView = this._getProductsInfo();
    const basicInfo = this._getBasicInfoAsText();
    const actionCard = this._getActionCardInfo();

    // If this is the first time we're creating the overlay content, create it and let it fade in.
    if (!this._overlayContent) {
      this._overlayContent = document.createElement('div');
      const el = this._overlayContent;
      el.classList.add('overlay-content');
      el.style.opacity = '0';
      el.style.transition = 'opacity 200ms';
      setTimeout(() => {
        el.style.opacity = '1';
      }, 1);
      overlay.appendChild(el);
      // The overlay content fading in is the signal that the player has
      // real metadata to render. The expand button (when configured) rides
      // along on the same beat so it doesn't flash before anything is on
      // screen.
      this.wrapper?.classList.add('has-preview');
    }

    this._overlayContent.innerHTML = `
      ${closedCaptions}
      ${productView ? productView : basicInfo}
      ${actionCard}
      ${this._getSocialMediaAttribution()}
    `;

    this._updateActionListeners();
    this._updateProductListeners();
    this._updateSocialMediaAttributionListener();
    this._applyClosedCaptions();
  }

  _updateSocialMediaAttributionListener() {
    // The bam-player wrapper has a global click handler that expands the
    // player; stop here so tapping the attribution link opens the source
    // post permalink (target="_blank") without also expanding playback.
    const link = this.overlay.querySelector('a.social-media-attribution');
    if (!link) return;
    link.addEventListener('click', (e) => e.stopPropagation());
  }

  _createClosedCaptions() {
    // Always render the container, regardless of merchant CC config. The
    // viewer can toggle captions on inside the expanded player; this gives the
    // closedcaptionupdate message handler a target to write into when they
    // collapse back to the inline preview. Container self-hides via .empty
    // when there's no text, so this costs nothing visually when CC is off.
    return `
      <div class="closed-captions empty" aria-live="polite">
        <span class="caption-text"></span>
      </div>
    `;
  }

  _updateClosedCaptions({ caption } = {}) {
    if (caption === this._currentCaption
      || (!caption && !this._currentCaption)) return;

    this._currentCaption = caption || '';
    this._applyClosedCaptions();
  }

  _applyClosedCaptions() {
    const captionTextElement = this.overlay.querySelector('.caption-text');
    if (!captionTextElement) return;

    captionTextElement.textContent = this._currentCaption || '';
    captionTextElement.closest('.closed-captions')?.classList.toggle('empty', !this._currentCaption);
  }

  _updateActionListeners() {
    const actionCards = this.overlay.querySelectorAll('.action');
    for (const actionCard of actionCards) {
      const actionCardHandler = (e) => {
        if (e.key && e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        this._actionClickHandler(actionCard.id);
      };

      actionCard.addEventListener('click', actionCardHandler);
      actionCard.addEventListener('keydown', actionCardHandler);
    };
  }

  _actionClickHandler(actionId) {
    const action = this._actions.find(({id}) => id === actionId);
    if (!action) {
      console.error(`Action not found for ${actionId}`);
      return;
    }

    switch (action.type) {
      case ACTION_TYPE.LINK:
      default:
        // Temporary solution until we know the data structure of actions
        const rawUrl = action?.data?.url;
        if (!rawUrl) {
          console.error(`Can't perform action - url not found - ${actionId}`);
          return;
        }

        // If the url starts with `https://`, `http://`, or `//` - leave it as is,
        // otherwise prepend `//` to the url
        const url = /^(https?:\/\/|\/\/)/i.test(rawUrl) ? rawUrl : `//${rawUrl}`;

        const actionTarget = action?.data?.target || '_blank';

        // Open the URL in the same tab
        // TODO: Introduce configurable behavior
        this._trackMetric('on-interaction', {
          interactionType: 'trigger-action',
          actionId,
          ...(action.type && { actionType: action.type }),
          ...(actionTarget && { actionTarget }),
        });

        const { playerDelegate } = this.embedInstance;
        const actionCardClickedEventName = PLAYER_EVENTS.ACTION_CARD_CLICKED.replace(PLAYER_EVENT_PREFIX, '');
        const actionCardClickedEventCallbacks = playerDelegate?.eventSubscribers?.[actionCardClickedEventName] || [];
        for (const callback of actionCardClickedEventCallbacks) {
          try {
            callback({
              actionId,
              url,
              // Announce to the callback how this action is configured in the backend, using the same
              // human readable values as configured in the BamHub UI for setting up this action card.
              target: actionTarget === '_top' ? 'current-tab' : 'new-tab',
            });
          } catch (err) {
            console.warn('Failed to call action card clicked callbacks', { err });
          }
        }
        const { openActionCard } = this._getPlayerIntegrationConfig();
        if (openActionCard === 'none') return;

        window.open(url, actionTarget);
        break;
    }
  }

  _updateProductListeners() {
    const productsContainer = this.overlay.querySelector('.products');

    if (!productsContainer) return;

    const productCards = this.overlay.querySelectorAll('.product');

    for (const productCard of productCards) {
      productCard.addEventListener('click', (e) => {
        this._productClickHandler(productCard.id, e);
      });

      productCard.addEventListener('keydown', (e) => {
        if (e.target !== productCard) return;
        if (e.key !== 'Enter') return;
        this._productClickHandler(productCard.id, e);
      });

      productCard.addEventListener('focus', () => {
        productCard?.scrollIntoView({
          block: "nearest",
          inline: "center",
          behavior: "smooth",
        });
      });
    }
  }

  _productClickHandler(productId, e, source) {
    const product = this._getPreviewProductById(productId);
    if (!product) {
      console.error(`Product not found for ${productId}`);
      return;
    }

    const { raw, ...productTrackingData } = product;
    this._trackMetric('on-interaction', {
      interactionType: 'show-product',
      productAction: this._attr.productAction,
      ...(source && { source }),
      ...productTrackingData,
    });

    // Emit SHOW_PRODUCT_VIEW when product is clicked in this component.
    // This will let the integration code know that the user clicked on a product,
    // and can act accordingly.
    const { playerDelegate } = this.embedInstance;
    const showProductViewEventName = PLAYER_EVENTS.SHOW_PRODUCT_VIEW.replace(PLAYER_EVENT_PREFIX, '');
    const showProductViewEventCallbacks = playerDelegate?.eventSubscribers?.[showProductViewEventName] || [];
    const {
      id,
      productReference: sku,
      publicUrl: url,
      title,
      vendor,
      hydrated,
    } = product;
    const publicProduct = hydrated?.product
      ? toPublicProductData({
        id,
        ref: sku,
        currency: hydrated.currency,
        locale: hydrated.locale,
        // Same template fallback as the player applies for inheritFromPlaceholder()
        product: {
          ...hydrated.product,
          name: hydrated.product.name || title,
          brandName: hydrated.product.brandName || product.brand,
          thumbnailUrl: hydrated.product.thumbnailUrl || product.thumbnail,
        },
      })
      : toPublicProductData({
        id,
        ref: sku,
        product: { name: title, brandName: product.brand, thumbnailUrl: product.thumbnail },
      });
    const eventPayload = {
      id,
      sku,
      url,
      title,
      ...(vendor && { vendor }),
      ref: sku,
      actionOrigin: source || 'player',
      actionTarget: this._attr.productAction,
      product: publicProduct,
      ...(raw && { raw }),
    };

    for (const callback of showProductViewEventCallbacks) {
      try {
        callback(eventPayload);
      } catch (err) {
        console.warn('Failed to call show product view callbacks', { err });
      }
    }

    const { openProductsTarget } = this._getPlayerIntegrationConfig();
    if (openProductsTarget === 'none') {
      // REMARK: We should probably honor this config to avoid the setter
      // of the href to be called, which would make very little sense
      // in a context where the bam-player is presented in a WebView
      // environment.
      e.stopPropagation();
      return;
    }

    if (this._attr.focusMode === FOCUS_MODE.NONE
      && [PRODUCT_ACTION_TYPE.EXPAND, PRODUCT_ACTION_TYPE.EXPAND_PRODUCT].includes(this._attr.productAction)) {
      console.warn(`Product action "${this._attr.productAction}" is not compatible with focus mode "none".`);
    } 

    // e.stopPropagation(); - here it means that the player won't expand
    if (this._attr.productAction === PRODUCT_ACTION_TYPE.EXPAND) {
      // Do nothing here, the player expands via the click event on bamBundle scope
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.EXPAND_PRODUCT) {
      this.showProductViewInExpandedPlayer(product.id);
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.MODAL) {
      e.stopPropagation();
      this.showProductViewInBamProduct(product.id, product);
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.DRAWER) {
      e.stopPropagation();
      this.showProductViewInBamProduct(product.id, product);
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.NONE) {
      e.stopPropagation();
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.TAB_CURRENT) {
      e.stopPropagation();
      window.location.href = product.publicUrl;
      return;
    } else if (this._attr.productAction === PRODUCT_ACTION_TYPE.TAB_NEW) {
      e.stopPropagation();
      window.open(product.publicUrl, '_blank');
      return;
    }

    this._triggerClickProductEvent(product.id, product);
  }

  // Fetch the product-catalog `raw` feed for cataloged preview products and merge it
  // in, so bam-products product-view events can carry the same `raw` the in-player
  // SHOW_PRODUCT_VIEW event exposes. Gated on the integration NOT owning hydration.
  async _fetchAndApplyCatalogRaw(productsDataRequestId) {
    try {
      const { playerDelegate } = this.embedInstance || {};
      const provideProductDataEventName = PLAYER_EVENTS.PROVIDE_PRODUCT_DATA.replace(PLAYER_EVENT_PREFIX, '');
      const hasProvideProductData = (playerDelegate?.eventSubscribers?.[provideProductDataEventName] || []).length > 0;
      // Integration owns product data — don't reach out to product-catalog.
      if (hasProvideProductData) return;

      const orgId = this._event?.orgId;
      if (!orgId || !Array.isArray(this._products)) return;

      // Only cataloged products (those with a productId) have a catalog `raw` feed.
      const catalogedProducts = this._products.filter((p) => (
        p && hasProductId(p.id) && p.productId && !p.raw
      ));
      if (!catalogedProducts.length) return;

      const rawById = await this._requestCatalogRaw(orgId, catalogedProducts);
      // A newer products payload superseded this one while the fetch was in flight.
      if (this._productsDataRequestId !== productsDataRequestId) return;
      if (!rawById || rawById.size === 0) return;

      // Merge into both the canonical list and the rendered snapshot. No re-render:
      // raw isn't displayed, and the click handler reads products live at click time.
      this._products = mergeCatalogRawIntoProducts(this._products, rawById, orgId);
      this._lastRenderablePreviewProducts = mergeCatalogRawIntoProducts(
        this._lastRenderablePreviewProducts,
        rawById,
        orgId,
      );
    } catch (err) {
      console.warn('Failed to enrich products with catalog raw data', err);
    }
  }

  // Query product-catalog (phV4) for the given cataloged products and build a
  // feed + sku -> { raw, vendor } map. Splits into multiple requests when the query
  // would exceed a safe URL length, mirroring ProductTemplatesService.requestCatalogProducts.
  async _requestCatalogRaw(orgId, products) {
    const MAX_URL_LENGTH = 2000;
    const baseUrl = `${process.env.WIDGETS_APPENGINE_BASE_URL}/product-catalog/products`;

    const fetchChunk = async (chunk) => {
      if (!chunk.length) return [];
      const query = buildCatalogRawSearchParams(chunk, orgId).toString();
      const url = `${baseUrl}?${query}`;
      // Only split while there's more than one product to place (avoids infinite recursion).
      if (url.length > MAX_URL_LENGTH && chunk.length > 1) {
        const mid = Math.ceil(chunk.length / 2);
        const [first, second] = await Promise.all([fetchChunk(chunk.slice(0, mid)), fetchChunk(chunk.slice(mid))]);
        return [...first, ...second];
      }
      return window.fetch(url)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => json?.products || [])
        .catch(() => []);
    };

    return catalogProductsToRawMap(await fetchChunk(products), orgId);
  }

  _getPreviewProductById(productId) {
    if (!Array.isArray(this._products)) return undefined;
    if (!hasProductId(productId)) return undefined;

    const productIdKey = String(productId);
    const productIdMatches = ({ id } = {}) => (
      hasProductId(id) && String(id) === productIdKey
    );
    const renderedProduct = this._lastRenderablePreviewProducts?.find(productIdMatches);
    if (renderedProduct) return renderedProduct;

    const displayedProducts = getRenderablePreviewProducts({
      products: this._products,
      productsToHydrate: this._productHydrationState?.productsToHydrate,
      fallbackProducts: this._lastRenderablePreviewProducts,
    });
    return displayedProducts.find(productIdMatches)
      || this._products.find(productIdMatches);
  }

  _triggerClickProductEvent(productId, productData) {
    const videoSourceId = this._attr.videoId || this._attr.eventId;
    const detailEventIdKey = this._attr.videoId ? 'videoId' : 'eventId';
    const resolvedProductData = productData || this._getPreviewProductById(productId);
    const detail = {
      [detailEventIdKey]: videoSourceId,
      productId,
      productData: resolvedProductData,
      theme: this._theme,
      integrationConfig: this._getPlayerIntegrationConfig(),
      playerUrl: this._getPlayerUrl(),
      event: this._event,
      orgSettings: this._orgSettings,
      withCloseButton: true,
      embedInstanceKey: this.embedInstance?.instanceKey,
      eventHandlers: this.embedInstance?._playerEventHandlers || [],
    };
    this.dispatchEvent(new CustomEvent('clickproduct', { detail }));
  };

  _setupDOM() {
    // Make sure we start from a clean slate
    this.shadow.innerHTML = '';

    const styleSheet = document.createElement('style');
    this.styleSheet = styleSheet;

    const placeholderLowRes = document.createElement('div');
    placeholderLowRes.classList.add('placeholder-low-res');

    const placeholder = document.createElement('div');
    placeholder.classList.add('placeholder');

    const overlay = document.createElement('div');
    overlay.classList.add('overlay');
    this.overlay = overlay;

    const wrapper = document.createElement('div');
    wrapper.classList.add('wrapper');
    wrapper.tabIndex = 0;
    wrapper.setAttribute('role', 'button');
    this.wrapper = wrapper;

    // TODO: support these iframe attributes:
    // sandbox
    // credentialless
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allow', 'fullscreen; autoplay;');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowTransparency', '1');
    iframe.setAttribute('title', 'Live video shopping');
    this.iframe = iframe;

    const expandBtn = document.createElement('button');
    expandBtn.type = 'button';
    expandBtn.classList.add('expand-btn');
    expandBtn.setAttribute('aria-label', 'Expand player');
    expandBtn.dataset.visibility = this._settings.expandButtonVisibility;
    expandBtn.innerHTML = ExpandIcon;
    expandBtn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
    });
    expandBtn.addEventListener('click', (e) => {
      const fm = this._attr.focusMode;
      // For carousel/single, the wrapper-level _onClick handler and the
      // bundle's bam-player click handler together produce the full normal
      // expand behavior (enter carousel / single-mode setup + load UI).
      // Let the click bubble so both fire — same path as clicking the body.
      if (fm === FOCUS_MODE.CAROUSEL || fm === FOCUS_MODE.SINGLE) return;
      // For playpause the wrapper click is unbound (standalone='false') and
      // the bundle handler toggles play/pause; for none the wrapper short-
      // circuits. Emit a custom event so bamBundle can drive the full
      // playlist-level expansion (same as carousel mode does via _expand()).
      e.stopPropagation();
      this.dispatchEvent(new CustomEvent('expand-button-click', { bubbles: true, composed: true }));
    });
    this.expandBtn = expandBtn;

    wrapper.appendChild(iframe);
    wrapper.appendChild(placeholderLowRes);
    wrapper.appendChild(placeholder);
    wrapper.appendChild(overlay);
    wrapper.appendChild(expandBtn);

    // Batch append the players to the DOM using requestAnimationFrame to avoid blocking
    // the main thread for too long if many players are being added at once.
    if (!window.__bamPlayerInjectQueue) window.__bamPlayerInjectQueue = [];
    window.__bamPlayerInjectQueue.push(this);
    if (!window.__bamPlayerAnimationFrame) {
      window.__bamPlayerAnimationFrame = true;
      window.requestAnimationFrame(() => {
        window.__bamPlayerInjectQueue.forEach((player) => {
          player.shadow.appendChild(player.styleSheet);
          player.shadow.appendChild(player.wrapper);
          player._isWrapperInDOM = true;
          if (player._didDeferOnWrapperNotInDOM) {
            player._setup();
          }
        });
        window.__bamPlayerInjectQueue = [];
        window.__bamPlayerAnimationFrame = false;
      });
    }

    // Bump the CSS when the component changes in size. This is primarily to update the font size.
    if ('ResizeObserver' in window) {
      if (!window.__bamPlayerResizeObserver) {
        window.__bamPlayerResizeObserver = new window.ResizeObserver((entries) => {
          for (const entry of entries) {
            const bamPlayer = entry.target.bamPlayer;
            if (bamPlayer._isPlayerUILoaded) return;
            bamPlayer._updateStylesheet();
            bamPlayer._emit('playerResize');
            bamPlayer._sendExternalPlayerControllerResizeEvent();
          }
        });
      }
      wrapper.bamPlayer = this;
      window.__bamPlayerResizeObserver.observe(wrapper);
    }
  }

  _loadIframe({ forcePreload = false } = {}) {
    if (!this.iframe) {
      console.error('Iframe not found when trying to load Bambuser player');
      return;
    }

    // If the player is visible on the page, set attributes to promote quicker loading
    if (this._isVisibleOnPage()) {
      this.iframe.setAttribute('loading', 'eager');
      this.iframe.setAttribute('fetchpriority', 'high');
    }

    this.iframe.src = this._getPlayerUrl({ forcePreload });

    // Listen to events sent to us from the player page
    this._setupPlayerPageEventListeners();
  }

  _setupDOMEventListeners() {
    if (this._hasDOMEventListeners) return;
    this._hasDOMEventListeners = true;

    const { autoplay, standalone } = this._attr;

    const onUnloadCallbacks = [];
    onUnloadCallbacks.push(() => this._hasDOMEventListeners = false);

    if (standalone) {
      // The player is set to be standalone, meaning it itself is responsible for expanding
      // the player to full screen when clicked.
      const onClick = () => this._onClick();
      this.wrapper.addEventListener('click', onClick);
      onUnloadCallbacks.push(() => this.wrapper.removeEventListener('click', onClick));

      const onKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._onClick();
        }
      };
      this.wrapper.addEventListener('keydown', onKeyDown);
      onUnloadCallbacks.push(() => this.wrapper.removeEventListener('keydown', onKeyDown));
    }

    // When the autoplay property is set to "hover" (as opposed to boolean)
    // we'll play/pause the video when the user hovers over the iframe
    if (autoplay === 'hover') {
      const onHoverEvent = (event) => {
        if (this._isExpandedInPlaylist) return;

        if (event.type === 'mouseenter') {
          this.play();
        } else {
          this.pause();
        }
      };
      this.wrapper.addEventListener('mouseenter', onHoverEvent);
      this.wrapper.addEventListener('mouseleave', onHoverEvent);
      onUnloadCallbacks.push(() => {
        this.wrapper.removeEventListener('mouseenter', onHoverEvent);
        this.wrapper.removeEventListener('mouseleave', onHoverEvent);
      });
    }

    // Use a shared IntersectionObserver instance to track visibility of all bam-player instances
    this._shouldDisconnectObserverWhenVisible = true;
    this._debounceHandle;
    if (!window.__bamPlayerIntersectionObserver) {
      window.__bamPlayerIntersectionObserver = new window.IntersectionObserver((entries) => {
        for (const entry of entries) {
          const bamPlayer = entry.target.bamPlayer;
          clearTimeout(bamPlayer._debounceHandle);
          if (entry.isIntersecting) {
            bamPlayer._emit('visible');
            if (bamPlayer._shouldDisconnectObserverWhenVisible) {
              window.__bamPlayerIntersectionObserver.unobserve(bamPlayer.wrapper);
            }
            bamPlayer._hasBeenVisible = true; // Used to track if the player has been visible at some point
          } else {
            // Something causes the observer to announce isIntersecting=false a couple of times
            // in rapid succession after playback has started the first time. Let's debounce
            // the hidden event to avoid unnecessary (though invisible) pauses.
            bamPlayer._debounceHandle = setTimeout(() => {
              bamPlayer._emit('hidden');
            }, 200);
          }
        }
      });
    }
    window.__bamPlayerIntersectionObserver.observe(this.wrapper);
    onUnloadCallbacks.push(() => {
      if (window.__bamPlayerIntersectionObserver) {
        window.__bamPlayerIntersectionObserver.unobserve(this.wrapper);
      }
    });

    // When the autoplay property is set to "visible" we'll play/pause the video
    // when the iframe is visible in the viewport
    if (autoplay === 'visible') {
      this._shouldDisconnectObserverWhenVisible = false;
      if (this._isVisibleOnPage()) {
        this.play();
      }

      // When the autoplay property is set to "visible" (as opposed to boolean)
      // we'll play/pause the video when the iframe is visible in the viewport
      const onVisible = () => this.play();
      const onHidden = () => this.pause();
      this.addEventListener('visible', onVisible);
      this.addEventListener('hidden', onHidden);
      onUnloadCallbacks.push(() => {
        this._shouldDisconnectObserverWhenVisible = true;
        this.removeEventListener('visible', onVisible);
        this.removeEventListener('hidden', onHidden);
      });
    }

    this._unloadDOMEventListeners = () => {
      onUnloadCallbacks.forEach((handler) => handler());
    };
  }

  _isAboveFold() {
    if (!this.wrapper) return false;
    const { top, bottom } = this.wrapper.getBoundingClientRect();
    return top < window.innerHeight && bottom >= 0;
  }

  _isVisibleOnPage() {
    if (!this.wrapper) return false;
    if ('checkVisibility' in this.wrapper) {
      if (!this.wrapper.checkVisibility()) {
        // The element is not visible even though it may be in the viewport
        return false;
      }
    }

    if (!this._isAboveFold()) return false;

    return true;
  }

  _isHorizVisibleInContainer() {
    if (!this.wrapper) return false;
    const container = this._getScrollContainer();
    if (!container) return null;

    const er = this.wrapper.getBoundingClientRect();
    const cr = container.getBoundingClientRect();
    const base = cr.left + container.clientLeft;

    // element bounds in the container's scroll space
    const x1 = er.left - base + container.scrollLeft;
    const x2 = er.right - base + container.scrollLeft;

    // visible interval of the scrollport (in the same space)
    const visL = container.scrollLeft;
    const visR = container.scrollLeft + container.clientWidth;

    return x2 > visL && x1 < visR;
  }

  _getScrollContainer() {
    if (this._scrollContainer) return this._scrollContainer;
    // Find the closest "player-container" element
    for (let p = this.parentElement; p; p = p.parentElement) {
      if (p.classList.contains('player-container')) {
        this._scrollContainer = p;
        break;
      }
    }
    return this._scrollContainer; // viewport
  }

  setMediaAssets(mediaAssets) {
    const firstAsset = mediaAssets && mediaAssets[0];
    if (!firstAsset) return;

    if (!isNaN(firstAsset.data?.width) && !isNaN(firstAsset.data?.height)) {
      this._videoWidth = firstAsset.data.width;
      this._videoHeight = firstAsset.data.height;
    }

    if (firstAsset.previewLowResBlob) {
      this._posterLowResBlob = firstAsset.previewLowResBlob;
    }

    if (firstAsset.data?.preview) {
      this._poster = firstAsset.data.preview;
    }

    // Source-platform attribution. Mirrors parseVodBootstrapData in
    // player/src/index.ejs so the preview overlay can render the full
    // attribution badge (logo + @handle + Sponsored) before the iframe
    // has loaded and pushed its broadcastData over postMessage. Without
    // this, the badge degrades to logo-only on the first frame and only
    // catches up once the iframe is ready.
    if (firstAsset.type === 'tiktok') {
      this._sourcePlatform = 'tiktok';
      this._author = firstAsset.data?.author || firstAsset.data?.author_name || null;
      this._permalink = firstAsset.data?.permalink || null;
      this._hasUgcRights = !!firstAsset.hasUgcRights;
    } else if (firstAsset.type === 'baas' && firstAsset.sourcePlatform === 'instagram') {
      const importData = firstAsset.importData || {};
      this._sourcePlatform = 'instagram';
      this._author = importData.username || null;
      this._permalink = importData.permalink || null;
      this._hasUgcRights = !!firstAsset.hasUgcRights;
    } else if (firstAsset.type === 'image' && firstAsset.sourcePlatform) {
      const importData = firstAsset.importData || {};
      this._sourcePlatform = firstAsset.sourcePlatform;
      this._author = importData.username || null;
      this._permalink = importData.permalink || null;
      this._hasUgcRights = !!firstAsset.hasUgcRights;
    }

    // The overlay may have already been rendered (via the `settings`
    // attributeChangedCallback during init) before this call lands the
    // social-media fields on the instance. Without an explicit re-render
    // here the badge stays stuck on whatever was current at that earlier
    // render — typically logo-only, since `_author` / `_permalink` arrive
    // through this very call. Mirror the broadcastData handler's nudge.
    if (this._overlayContent) this._updateOverlayDOM();
  }

  getVisibleArea() {
    if (!this.wrapper) return 0;
    if (!this._isVisibleOnPage()) return 0;

    const { top, bottom, left, right, width, height } = this.wrapper.getBoundingClientRect();

    const visibleHeight = Math.min(window.innerHeight, bottom) - Math.max(0, top);
    const visibleWidth = Math.min(window.innerWidth, right) - Math.max(0, left);

    const visiblePlayerArea = visibleWidth * visibleHeight;
    const totalPlayerArea = width * height;

    const visibleArea = Math.ceil(100 * (visiblePlayerArea / totalPlayerArea)) / 100;

    return visibleArea;
  }

  positionRelativeToCenter() {
    if (!this.wrapper) return false;
    if (!this._isVisibleOnPage()) return false;

    const CENTERING_THRESHOLD = 15;
    const { left, width } = this.wrapper.getBoundingClientRect();

    const windowCenter = window.innerWidth / 2;
    const playerCenter = left + (width / 2);

    // if central line of the player is farther than central line of the window
    // by more than CENTERING_THRESHOLD pixels - then it's not centered
    const isCenteredHorizontally = Math.abs(playerCenter - windowCenter) < CENTERING_THRESHOLD;

    return isCenteredHorizontally
      ? HORIZONTAL_POSITION.CENTER
      : (playerCenter <= windowCenter ? HORIZONTAL_POSITION.LEFT : HORIZONTAL_POSITION.RIGHT);
  }

  _setupPlayerPageEventListeners() {
    this.__onPlayerIframeMessage = this._onPlayerIframeMessage.bind(this);
    PostMessageConduit.registerIframeListener(this.iframe, this.__onPlayerIframeMessage);
  }

  async _onPlayerIframeMessage({ eventName, data, isVod }) {
    if (eventName === 'baas-player:ready') {
      this._emit('playerReady');
    }

    if ([
      'baas-player:eventData',
      'baas-player:orgSettings',
      'baas-player:theme',
      'baas-player:customizations',
      'baas-player:broadcastData',
      'baas-player:products',
      'baas-player:actions',
    ].includes(eventName)) {
      return await this._handleBaaSDataMessage(eventName, data);
    }

    // TODO: remove the above and just use baas-player:bootstrap going forward
    if (eventName === 'baas-player:bootstrap') {
      Object.entries(data || {}).forEach(([key, value]) => {
        this._handleBaaSDataMessage(`baas-player:${key}`, value);
      });
      return;
    }

    if (eventName === 'baas-player:closedcaptionupdate') {
      this._updateClosedCaptions(data);
    }

    if (eventName === 'baas-player:playing') {
      const wasPlaying = this._isPlaying;
      this._isPlaying = true;
      // Playback is running again, so the next end-of-playback is a new one.
      // Keyed off the iframe's own event rather than our play() so it also
      // covers a start we didn't initiate (the play button inside the UI).
      this._endedHandled = false;

      if (!wasPlaying && isVod) {
        // If currentTime is 0 or NaN, the video is starting from the beginning,
        // otherwise it's a resuming playback
        const metric = isNaN(this._currentTime) || this._currentTime === 0
          ? 'on-play'
          : 'on-resume';
        this._trackMetric(metric);
        // Only emit 3 second progress if the video is starting from the beginning
        // If the player is expanded before the 3 second mark, we'll emit the 3 second
        // in the player UI instead
        if (metric === 'on-play') {
          this._emitOnProgress(3, false);
        }
        this._emitOnProgress(30, true);
      }
      this.wrapper.classList.add('is-playing');
      this.wrapper.classList.add('played-at-least-once');
      this.wrapper.classList.add('has-preview');

      this._emit('playing');
    }

    if (eventName === 'baas-player:pause') {
      const wasPlaying = this._isPlaying;
      this._isPlaying = false;
      if (!this._isPlayerUILoaded) {
        this.wrapper.classList.remove('is-playing');
      }
      if (wasPlaying && isVod && !this._isExpandedInPlaylist) {
        this._trackMetric('on-pause');
      }

      this._emit('pause');
      this._clearProgressEmissions();
    }

    if (eventName === 'baas-player:ended') {
      this._isPlaying = false;
      if (isVod) {
        this._trackMetric('on-stop');
      }

      // If the player UI is loaded, we'll handle the ended via playback-status event
      if (!this._isPlayerUILoaded) this._onEnded();

      this._clearProgressEmissions();
    }

    if (eventName === 'baas-player:timeupdate') {
      if (!this._hasReceivedTimeUpdate) {
        // The first time we receive a time update, we can consider the player ready to play.
        // There are more suitable events, but this'll do.
        this._hasReceivedTimeUpdate = true;
        if (this._shouldPlay) {
          // play() was called when the player wasn't ready
          this.play();
        }
      }
      this._currentTime = data?.currentTime || 0;
      this._emit('timeupdate');
    }

    if (eventName === 'baas-player:error') {
      if (data === 'viewsQuotaReached:vod') {
        console.error('Shoppable video views quota reached');
        const errorEl = document.createElement('div');
        const errorStyle = errorEl.style;
        const style = { color: 'white', fontSize: 20, position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' };
        for (const prop in style) {
          errorStyle[prop] = style[prop];
        }
        errorEl.textContent = 'You have reached your quota of views for shoppable videos.';
        this.shadow.appendChild(errorEl);
      }
      this._playerReady.resolve();
    }

    // external player events API
    if (eventName.startsWith('baas-player:externalPlayerController:')) {
      // invoke already configured external player controller events,
      // or if we don't have any explicitly defined events for some event
      // just emit it as a custom event for the integration to pick up
      this._handleExternalPlayerControllerEvents({
        eventName,
        data,
      });
    }
  }

  async _handleBaaSDataMessage(eventName, data) {
    switch(eventName) {
      case 'baas-player:eventData':
        this._playerReady.resolve();
        this._event = data;
        this.embedInstance?.updateTrackingContextData({
          orgId: this._event.orgId,
        });
        this._updateOverlayDOM();
        this._updateStylesheet();

        // Log the initialization of the web component including the attributes
        // Defer until the player has been visible in the viewport at least once
        const trackOnVisible = () => {
          this._trackMetric('on-initialized', {
            ...this._attr,
            ...(this._attr.videoId ? {
              vod: {
                ...this._vodTrackingPayloadWithMetadata,
              },
            } : {}),
          });
          this.removeEventListener('visible', trackOnVisible);
        };
        if (this._hasBeenVisible) {
          trackOnVisible();
        } else {
          this.addEventListener('visible', trackOnVisible);
        }
        break;

      case 'baas-player:orgSettings':
        this._orgSettings = data;
        break;

      case 'baas-player:theme':
        this._theme = data;
        this._updateStylesheet();
        if (this._overlayContent) this._updateOverlayDOM();
        break;

      case 'baas-player:customizations':
        this.dispatchEvent(
          new CustomEvent('customizations-loaded', {
            detail: {
              customizations: data,
            },
          }),
        );
        break;

      case 'baas-player:broadcastData':
        this._poster = data?.preview;
        this._posterLowResBlob = data?.previewLowResBlob || this._posterLowResBlob;
        this._videoWidth = data?.width;
        this._videoHeight = data?.height;
        this._broadcastId = data?.id;
        // Social-media-sourced broadcasts (TikTok, Instagram) surface the
        // source platform, creator handle and post permalink alongside the
        // usual broadcast fields — pick them up so the preview overlay can
        // render an attribution badge and trim a redundant author prefix
        // from the title.
        this._sourcePlatform = data?.sourcePlatform || null;
        this._author = data?.author || null;
        this._permalink = data?.permalink || null;
        this._hasUgcRights = !!data?.hasUgcRights;

        // For image broadcasts, the poster IS the still and the iframe
        // renders it with a Ken Burns FROM transform. Apply the same
        // transform to the placeholder's background-image so the preview
        // tile visually matches the iframe's resting frame — no jump when
        // the iframe fades in on expand. The transform string is computed
        // player-side (ImagePlayer.transformForFocalPoint) and forwarded on
        // the broadcast payload so this side doesn't duplicate the math or
        // the Ken Burns defaults.
        if (data?.posterTransform && this.wrapper) {
          const placeholderEl = this.wrapper.querySelector('.placeholder');
          if (placeholderEl) {
            placeholderEl.style.transformOrigin = 'center center';
            placeholderEl.style.transform = data.posterTransform;
          }
        }

        this._isReady = true;
        this.setScaleMode('aspectFill');
        this.setObjectPosition('center');

        // Adjust the aspect ratio of the iframe to match the broadcast
        // and set the width of the wrapper to match the broadcast width
        this._updateStylesheet();
        // Re-render the overlay so the attribution badge / trimmed title
        // appear even if broadcastData arrived after eventData.
        if (this._overlayContent) this._updateOverlayDOM();

        // If autoplay=visible and the element happens to visible now that the
        // player is ready we should start playback
        if (this._attr.autoplay === 'visible' && this._isVisibleOnPage()) {
          this.play();
        }
        break;

      case 'baas-player:products':
        await this._handleProductsDataMessage(data);
        break;

      case 'baas-player:actions':
        this._actions = data;
        this._updateOverlayDOM();
        break;

      default:
        break;
    }
  }

  async _handleProductsDataMessage(data) {
    const productsDataRequestId = this._advanceProductsDataRequestId();
    this._cleanupProductHydration();
    this._products = data;

    // Preview rendering and clicks do not wait on catalog enrichment. Bootstrap raw is
    // available immediately; the fallback enriches later clicks on live and older payloads.
    const hasMissingCatalogRaw = Array.isArray(this._products)
      && this._products.some((product) => product?.productId && !product.raw);
    if (hasMissingCatalogRaw) this._fetchAndApplyCatalogRaw(productsDataRequestId);

    let hydrationPromise = Promise.resolve();
    try {
      hydrationPromise = this._requestProductHydration();
    } catch (err) {
      this._cleanupProductHydration();
      console.error('Failed to hydrate products', err);
    }
    const hasPendingHydration = !!this._productHydrationState;

    if (this._productsDataRequestId !== productsDataRequestId) return;

    // The first preview render may wait while every product is pending
    // hydration. The expanded player also treats pending hydrated products as
    // not renderable, because hydration can mark products hidden.
    const hasRenderablePreviewProducts = getRenderablePreviewProducts({
      products: this._products,
      productsToHydrate: this._productHydrationState?.productsToHydrate,
      fallbackProducts: this._lastRenderablePreviewProducts,
    }).length > 0;

    if (this._overlayContent || !hasPendingHydration || hasRenderablePreviewProducts) {
      this._updateOverlayDOM();
    }

    try {
      await hydrationPromise;
    } catch (err) {
      console.error('Failed to hydrate products', err);
      if (this._productsDataRequestId === productsDataRequestId) {
        this._cleanupProductHydration();
      }
    }

    // Hydration can finish after a newer source has supplied products, or
    // after disconnect cleared the current request. Only the latest product
    // payload may re-render the overlay.
    if (this._productsDataRequestId !== productsDataRequestId) return;

    if (hasPendingHydration) this._updateOverlayDOM();
  }

  _advanceProductsDataRequestId() {
    this._productsDataRequestId = (this._productsDataRequestId || 0) + 1;
    return this._productsDataRequestId;
  }

  _handleExternalPlayerControllerEvents({ eventName, data }) {
    if (eventName === 'baas-player:externalPlayerController:play') {
      // External player integration expects SHOW and PLAY to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_SHOW, data });
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_PLAY, data });
    } else if (eventName === 'baas-player:externalPlayerController:pause') {
      // External player integration expects PAUSE to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_PAUSE, data });
    } else if (eventName === 'baas-player:externalPlayerController:load') {
      // External player integration expects LOAD to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_LOAD, data });
    } else if (eventName === 'baas-player:externalPlayerController:autoplay') {
      // External player integration expects SHOW and PLAY to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_SHOW, data });
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_PLAY, data });
    } else if (eventName === 'baas-player:externalPlayerController:muted') {
      // External player integration expects MUTED to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_MUTE, data });
    } else if (eventName === 'baas-player:externalPlayerController:currentTime') {
      // External player integration expects SEEK to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_SEEK, data });
    } else if (eventName === 'baas-player:externalPlayerController:controls') {
      // External player integration expects CONTROLS to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_CONTROLS, data });
    } else if (eventName === 'baas-player:externalPlayerController:scaleMode') {
      // External player integration expects SCALE_MODE to be called
      this._callExternalPlayerControllerCallbacks({ publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_SCALE_MODE, data });
    }
  }

  _callExternalPlayerControllerCallbacks({ publicEventName, data }) {
    if (!publicEventName) return;
    const { playerDelegate } = this.embedInstance;
    const externalPlayerEventName = publicEventName.replace(PLAYER_EVENT_PREFIX, '');
    const externalPlayerEventCallbacks = playerDelegate.eventSubscribers[externalPlayerEventName];
    if (!externalPlayerEventCallbacks?.length) return;

    for (const callback of externalPlayerEventCallbacks) {
      try {
        callback(data);
      } catch (err) {
        console.warn('Failed to call external player event callback', { err });
      }
    }
  }

  _sendExternalPlayerControllerResizeEvent() {
    if (this._isExpandedInPlaylist) {
      // Player UI should handle resizes
      return;
    }

    if (!this.embedInstance?.playerDelegate) {
      // Not ready yet
      return;
    }

    const { width: containerWidth, height: containerHeight } = this.wrapper.getBoundingClientRect() || {};

    if (!containerWidth || !containerHeight) {
      // No container dimensions available yet, so we can't resize the player
      return;
    }

    if (!this._videoWidth || !this._videoHeight) {
      // No video dimensions available yet, so we can't resize the player
      return;
    }

    const data = {
      gravity: 'top',
      width: containerWidth,
      height: containerHeight,
      id: this._broadcastId,
      scaleMode: 'aspectFill',
      left: 0,
      top: 0,
    };

    this._callExternalPlayerControllerCallbacks({
      publicEventName: PLAYER_EVENTS.EXTERNAL_PLAYER_CONTROLLER_RESIZE, data
    });
  };

  _setup() {
    if (this._setupComplete) return;
    this._cleanupProductHydration();
    this._advanceProductsDataRequestId();
    this._setupStarted = true;
    this._isReady = false;
    this._event = null;
    this._products = null;
    this._lastRenderablePreviewProducts = null;
    this._actions = null;
    this._isPlayerUILoaded = false;
    this._currentTime = this._attr.videoId ? 0 : undefined;
    this._endedHandled = false;
    clearTimeout(this._loopRestartHandle);
    this._loopRestartHandle = undefined;

    if (this._attr.deferLoad) {
      return;
    }

    if (!this._didDefer && document.readyState === 'loading') {
      // We should defer loading the player until the DOM is ready
      this._didDefer = true;
      document.addEventListener('DOMContentLoaded', () => this._setup());
      return;
    }

    if (!this._isWrapperInDOM) {
      // Wrapper may not yet be in the DOM if setup() is called
      // This can happen more often in prod-environments (or labs)
      this._didDeferOnWrapperNotInDOM = true;
      return;
    }

    // Remove any previous event listeners
    this._unloadDOMEventListeners?.();
    this._unloadPlayerEventListeners?.();

    if (!this._attr.eventId && !this._attr.videoId) return;
    if (this._attr.eventId && this._attr.videoId) {
      throw new Error('Both eventId and videoId cannot be set at the same time');
    }

    // Setup the instance of BambuserLiveShopping (embed.js) that will handle the full player once it's opened
    this._createEmbedInstance();

    // Setup event listeners for the DOM
    this._setupDOMEventListeners();

    this._updateSettingsFromAttributes();

    this._updateStylesheet();

    // the iframe itself should not be focusable
    // we focus on wrapper and some ui in the wrapper in the preview mode
    // and when in expanded mode - we focus on the content inside the iframe
    this.iframe.setAttribute('tabindex', -1);

    if (!window.__bamPlayerPriorityQueue) {
      window.__bamPlayerPriorityQueue = new PriorityQueue();
    }

    // Queue up the loading of the player app in the iframe

    // Determine a loading priority for this player based on the parent playlist's
    // index and our index within that playlist. Players earlier in the playlist will
    // get a higher priority than players later in the playlist. And the very first
    // few players in the playlist will get a higher priority than the rest.
    const batchIndex = (() => {
      if (this._loadCalledBeforeSetup) {
        // If load() was called before setup(), this player should get highest priority
        return 0;
      }
      if (this._inPlaylistIndex === 0) {
        // First player in any playlist gets highest priority
        return this._playlistIndex;
      }
      if (this._inPlaylistIndex <= 5) {
        // First handful of players in any playlist get higher priority
        return this._playlistIndex * 50;
      }
      return this._playlistIndex * 100; // All other players get lower priority
    })();
    const priority = Number(`${batchIndex}.${String(this._inPlaylistIndex).padStart(4, '0')}`);

    // When the player iframe has loaded, we may want to load the player UI or preload the video
    this._playerReady.promise.then(() => {
      if (this._shouldLoadPlayerUI) {
        this._loadPlayerUI();
      } else if (priority < 100) {
        // For high priority players, also trigger preloading of the actual video
        this.load();
      }
    });

    window.__bamPlayerPriorityQueue.add(this, () => {
      if (this.iframe.src) {
        // Already loaded
        return Promise.resolve();
      }

      // Load the player in the iframe when it's our turn
      this._loadIframe();
      this._onIframeLoaded();

      // Then when we have a green light that the player has loaded, tell
      // the queue we're done.
      return this._playerReady.promise;
    }, priority);

    this._setupComplete = true;
  }

  _onIframeLoaded() {
    // Intentionally empty
    // Lifecycle hook-method for subclasses
  }

  _createEmbedInstance() {
    const { eventId: eid, videoId } = this._attr;
    const eventId = eid || videoId; // Map videoId to eventId to piggypack on existing logic
    if (!eventId) return;

    const previousInstanceKey = this.embedInstance?.instanceKey;

    if (this.embedInstance) {
      this.embedInstance?.destroy();
    }

    const embedInstance = new BambuserLiveShopping({
      isVod: Boolean(!eid && videoId),
      previewOrgId: this._previewOrgId,
      eventId,
      isWebComponent: true,
      type: 'overlay',
      containerNode: this.wrapper, // Use our DOM element instead of letting embed instance create one
      destroyOnAppUnload: !!this._attr.standalone, // If standalone, let embed clean up fully on unload
      _collectMetrics: this._attr.collectMetrics,
      cookie: {
        domain: this._attr?.cookieDomain,
        activityCookieTTLDays: this._attr?.cookieTTLDays,
      },
      // if we support minimize,
      // we want to handle it fully via events and not have the embed instance also minimize the player
      minimizeEventOnly: this._withMinimizeSupport,
      ...(window.location.host === 'localhost:5080' && { playerUrl: 'http://local.bambuser.com/' }),
    });
    if (previousInstanceKey) {
      // Set instance key from first embed instance so we use same key externally for all subsequent embed instances
      embedInstance.instanceKey = previousInstanceKey;
    }
    this.embedInstance = embedInstance;

    // Calling mountPlayerAPI will make the embed instance call onBambuserLiveShoppingReady()
    this.embedInstance?.mountPlayerAPI(eventId, false, this);

    // Apply the provider config (player.configure({ this guy })) on the embed instance internal config object
    // This puts the userId/sessionId in the right places for them to be included
    // later when we call trackMetric()
    this.embedInstance?.applyProviderConfig();

    // Reset to the initial state when the user closes the player
    embedInstance.playerDelegate.on('close', () => {
      this._onClose();
      this._emit('close');
    });

    embedInstance.playerDelegate.on('playback-status', (data) => {
      if (data?.ended && !data?.seeking) {
        this._isPlaying = false;
        this._onEnded();
      }
    });

    embedInstance.playerDelegate.on(PLAYER_EVENTS_PUBLIC.MINIMIZE, (data) => {
      this.dispatchEvent(new CustomEvent('playerMinimize', { detail: data }));
    });

    // REMARK: Given the scenario that the integration handles the presentation of product view
    // or product list by being configured containing this...
    // ```javascript
    // buttons: {
    //   product: player.BUTTON.EVENT,
    //   productList: player.BUTTON.EVENT,
    // }
    // ```
    // ... then clicking one of those buttons will make trigger the corresponding show event,
    // but the hide event would never be called because the presentation happens outside of the web-player
    // implementation. This means that one day we might run into some integration using a bamBundle and
    // evaluate this instance's function `hasImportantUIShown` which would return true, even though there's
    // no guarantee the UI is presented anymore and the automatic swap to next player might be incorectly
    // stalled.
    embedInstance.playerDelegate.on('should-show-product-list', () => this._onProductListShown());
    embedInstance.playerDelegate.on('should-hide-product-list', () => this._onProductListHidden());
    embedInstance.playerDelegate.on('should-show-product-view', () => this._onProductViewShown());
    embedInstance.playerDelegate.on('should-hide-product-view', () => this._onProductViewHidden());

    embedInstance.playerDelegate.on('top-ui-element-shown', () => this._emit('top-ui-element-shown'));
    embedInstance.playerDelegate.on('top-ui-element-hidden', () => this._emit('top-ui-element-hidden'));

    // Redispatch swipe events
    embedInstance.playerDelegate.on('player-swipe-right', () => this._emit('swipe-right'));
    embedInstance.playerDelegate.on('player-swipe-left', () => this._emit('swipe-left'));
    embedInstance.playerDelegate.on('player-swipe-up', () => this._emit('swipe-up'));
    embedInstance.playerDelegate.on('player-swipe-down', () => this._emit('swipe-down'));

    embedInstance.playerDelegate.on('muted', () => this._onMutedFromPlayerUI());
    embedInstance.playerDelegate.on('unmuted', () => this._onUnmutedFromPlayerUI());
    embedInstance.playerDelegate.on('focus-web-component-outside-iframe', () => {
      this._emit('focus-web-component-outside-iframe');
    });
  }

  _onMutedFromPlayerUI() {
    this._emit('muted');
  }

  _onUnmutedFromPlayerUI() {
    this._emit('unmuted');
  }

  _onProductViewShown() {
    this._isProductViewShown = true;
    this._emit('show-product');
  }

  _onProductViewHidden() {
    this._isProductViewShown = false;
    this._emit('hide-product');
  }

  _onProductListShown() {
    this._isProductListShown = true;
  }

  _onProductListHidden() {
    this._isProductListShown = false;
  }

  hasImportantUIShown() {
    return this._isProductViewShown || this._isProductListShown;
  }

  // Public API for native SDK (webview) integrations that create a standalone
  // bam-player without a bam-playlist/bam-bundle ancestor. In the normal web
  // flow bam-bundle writes the appearance CSS variables on its :host and they
  // cascade into the nested player; standalone there's no such ancestor, so the
  // SDK hands us the distribution appearance values and we set the same
  // variables inline on our own host (they cascade into the shadow styles and
  // survive __updateStylesheet(), which only rewrites the shadow <style> node).
  applyExternalAppearance(config) {
    // Each call applies a complete appearance and fully replaces the previous
    // one — the SDK forwards the whole resolved distribution appearance config,
    // so there's no partial/accumulated state to reconcile. An omitted field
    // reverts: unconditional vars fall back to their default, conditional ones
    // are cleared so their CSS fallback applies.
    const safeConfig = getSafeAppearanceConfig(config, window.CSS.supports.bind(window.CSS));
    const vars = getAppearanceCssVars(safeConfig);
    const names = new Set(vars.map(([name]) => name));

    for (const name of CONDITIONAL_APPEARANCE_CSS_PROPS) {
      if (!names.has(name)) this.style.removeProperty(name);
    }
    for (const [name, value] of vars) {
      this.style.setProperty(name, value);
    }
  }

  _onClick() {
    if (!this._isReady) return;

    if (this._attr.focusMode === FOCUS_MODE.NONE) {
      // Do nothing on click if focusMode is set to "none"
      return;
    }

    this._activatePlayerUI();
  }

  _activatePlayerUI() {
    if (!this._isReady) return;

    const { playerDelegate } = this.embedInstance;
    const shouldExpandPreviewEventName = PLAYER_EVENTS.PREVIEW_SHOULD_EXPAND.replace(PLAYER_EVENT_PREFIX, '');
    const shouldExpandPreviewEventCallbacks = playerDelegate?.eventSubscribers?.[shouldExpandPreviewEventName] || [];
    for (const callback of shouldExpandPreviewEventCallbacks) {
      try {
        // TODO: In the scenario where there's many bam-player components on one page,
        // it would make sense to pass the actual bam-player as a reference here so
        // the integration can decide WHICH bam-player to expand (or do something entirely
        // different with)...
        callback();
      } catch (err) {
        console.warn('Failed to call should expand preview callbacks', { err });
      }
    }

    // Return early if bam-player component is configured
    // to not manually expand and load the player UI
    if (this._attr.disableExpandOnClick) return;

    this._loadPlayerUI();
    this.wrapper.classList.add('is-expanded');
  }

  focusFirstInteractiveElementInsideIframe() {
    this.embedInstance?.focusFirstInteractiveElementInsideIframe();
  }

  _onClose() {
    this._unloadPlayerUI();
    this.wrapper.classList.remove('is-expanded');

    // Remove some styling that were set by the embed instance
    this.wrapper.style = '';
  }

  _onEnded() {
    // One end-of-playback can reach us twice — via `playback-status` while the
    // player UI is loaded, and via `baas-player:ended` when it isn't — because
    // `_unloadPlayerUI()` can flip that flag between the two deliveries.
    // `baas-player:playing` re-arms us for the next pass.
    if (this._endedHandled) return;
    this._endedHandled = true;

    // Only loop while playback is still wanted. A tile the playlist has moved
    // off gets paused, and without this it would restart itself 500ms later
    // and keep looping next to whatever is supposed to be playing.
    if (this._attr.loop && !this._isPlayerUILoaded && this._shouldPlay) {
      // Restart playback after it ends
      // NOTE: Without the delay the player will get stuck in some weird loop and
      // play the last second of the video over and over again.
      clearTimeout(this._loopRestartHandle);
      this._loopRestartHandle = setTimeout(() => {
        this._loopRestartHandle = undefined;
        // A pause that lands inside the delay window clears the handle, but
        // re-check in case we were paused and re-armed within the same tick.
        if (!this._shouldPlay) return;
        this.setCurrentTime(0);
        this.play();
      }, 500);
    }

    this._emit('ended');
  }

  _tearDown() {
    this.shadow.innerHTML = '';
    this._cleanupProductHydration();
    this._advanceProductsDataRequestId();

    this._isReady = false;
    this._event = null;
    this._products = null;
    this._lastRenderablePreviewProducts = null;
    this._isPlayerUILoaded = false;
    this._currentTime = undefined;
    this._endedHandled = false;
    // A pending restart would otherwise play a player we've just torn down.
    clearTimeout(this._loopRestartHandle);
    this._loopRestartHandle = undefined;

    this._unloadDOMEventListeners?.();
    this._unloadPlayerEventListeners?.();
    this._updateOverlayDOM();

    if (this.__onPlayerIframeMessage) {
      PostMessageConduit.unregisterIframeListener(this.iframe, this.__onPlayerIframeMessage);
      delete this.__onPlayerIframeMessage;
    }

    this.embedInstance?.destroy();
    delete this.embedInstance;
  }

  _loadPlayerUI() {
    this._shouldLoadPlayerUI = true;

    if (!this.iframe.src) {
      // Player iframe hasn't loaded yet.
      this._loadIframe();
      return;
    }

    if (!this._event) {
      // Player iframe hasn't initialized yet, we would postMessage prematurely.
      // TODO: this._event is not the ideal indicator. Would be better to emit from the
      // player iframe when the React app is ready to be loaded.
      return;
    }

    if (this._isPlayerUILoaded) return;
    this._isPlayerUILoaded = true;

    // Stop listening to events on the web component itself. Let the player UI handle it from now on.
    this._unloadDOMEventListeners?.();

    // Beware! The methods called on this.embedInstance must be called in the correct order.

    if (this._attr.standalone) {
      this.embedInstance?.show(); // show() calls loadPlayer() internally
    } else {
      this.embedInstance?.loadPlayer();
    }

    // Tell player to load the React app (= the UI)
    this.iframe.contentWindow.postMessage('initReactApp', '*');

    if (this._isMuted === false || (this._isMuted === undefined && this._attr.startMuted === false)) {
      // Since the user has interacted with the video, we should be allowed to unmute now
      this.unmute();
    }

    this.play();

    this.wrapper.classList.add('player-ui-loaded');
    this._isPlayerUILoaded = true;

    if (!this._hasTrackedPlayerUILoaded) {
      this._hasTrackedPlayerUILoaded = true;
      this._trackMetric('on-interaction', { interactionType: 'load-player-ui' });
    }
  }

  _unloadPlayerUI() {
    this._shouldLoadPlayerUI = false;
    if (!this._isPlayerUILoaded) {
      return;
    }

    this.mute();

    this._isProductViewShown = false;
    this._isProductListShown = false;

    // Tell player to unload the React app (= the UI).
    // NOTE: The player-side implementation is, at the time of writing, a bit flaky.
    // We might need to do a full page reload in some cases.
    this.iframe.contentWindow.postMessage('unloadReactApp', '*');

    // Start listening to events on the web component again
    this._setupDOMEventListeners();

    this.wrapper.classList.remove('player-ui-loaded');
    this._isPlayerUILoaded = false;
    this.wrapper.style = '';

    // Wipe any closed captions that we might have been showing before
    // expanding the player. These would be outdated by now and the user
    // may have changed the closed captions settings in the player UI.
    this._updateClosedCaptions({ caption: null });
  }

  _emit(eventName) {
    const event = new window.CustomEvent(eventName);
    this.dispatchEvent(event);
  }

  _getTimelineForTracking() {
    // timeline field is not part of tracking schema
    // but it's useful to have it in the tracking data
    // and it's already added in the player's tracking data
    return {
      broadcastId: this._broadcastId,
    };
  }

  _trackMetric(metric, data) {
    // Never emit metrics if the player UI is loaded since it has it's
    // own metrics tracking service
    if (this._isPlayerUILoaded) return;
    const sourceAppPrefix = this._attr.videoId ? 'vod-' : '';
    this.embedInstance?._trackMetric(metric, {
      orgId: this._event?.orgId,
      playerVersion: 2,
      source: 'player',
      sourceApp: `${sourceAppPrefix}player-web-components`,
      isPlaying: this._isPlaying ?? false,
      schemaVersion: '2.0.0',
      clientTimestamp: new Date().toISOString(),
      videoCurrentTime: isNaN(this._currentTime) ? 0 : Math.floor(this._currentTime),
      distributionPageId: this._attr.distributionPageId,
      distributionContainerId: this._attr.distributionContainerId,
      timeline: this._getTimelineForTracking(),
      title: this._event?.title,
      ...(this._event && { isLive: this._event.isLive }),
      ...data,
      // If the data blob doesn't already contain the "vod" object and this tracking point
      // belongs to a vod video, append it to this tracking point
      ...(this._attr.videoId && !data?.vod ? { vod: { ...this._vodTrackingPayload } } : {}),
    });
  }

  _emitOnProgress(delay, repeat) {
    this._emitProgressTimeouts ??= {};
    const timeoutKey = `${delay}:${repeat}`;

    window.clearTimeout(this._emitProgressTimeouts[timeoutKey]);

    const cb = () => {
      this._trackMetric('on-progress');
      this._emitProgressTimeouts[timeoutKey] = repeat
        ? setTimeout(cb, delay * 1000)
        : null;
    };
    // Store the timeout with it's specific key so we don't clear
    // the wrong timeout if the delay is changed
    this._emitProgressTimeouts[timeoutKey] = setTimeout(cb, delay * 1000);
  }

  _clearProgressEmissions() {
    this._emitProgressTimeouts && Object.values(this._emitProgressTimeouts)?.forEach(i => window.clearTimeout(i));
  }

  _cleanupProductHydration() {
    const state = this._productHydrationState;
    if (!state) return;

    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }

    const { subscribers } = state;
    if (subscribers && state.resultHandler) {
      subscribers[INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT] = removeProductHydrationCallback({
        callbacks: subscribers[INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT],
        callbackToRemove: state.resultHandler,
      });
    }

    this._productHydrationState = null;
    state.resolve?.();
  }

  _requestProductHydration() {
    this._cleanupProductHydration();

    const { _products, _settings } = this;
    const { showProducts } = this._attr;
    const { playerDelegate } = this.embedInstance || {};
    const provideProductDataEventName = PLAYER_EVENTS.PROVIDE_PRODUCT_DATA.replace(PLAYER_EVENT_PREFIX, '');
    const productHydrationCallbacks = playerDelegate?.eventSubscribers?.[provideProductDataEventName] || [];
    const productsToHydrate = getPreviewProductsToHydrate({
      products: _products,
      showProducts,
      settingsProducts: _settings.products,
      hasProductHydrationCallbacks: productHydrationCallbacks.length > 0,
    });

    if (!productsToHydrate.length) return Promise.resolve();

    let resolveHydration;
    const hydrationPromise = new Promise((resolve) => { resolveHydration = resolve; });
    const finishHydration = () => this._cleanupProductHydration();
    const productHydrationResultHandler = ({ products = [] } = {}) => {
      const state = this._productHydrationState;
      if (!state || state.resultHandler !== productHydrationResultHandler) return;

      this._products = applyHydratedPreviewProducts({
        products: this._products,
        hydratedProducts: products,
      });

      const nextProductsToHydrate = getRemainingProductsToHydrate({
        productsToHydrate: state.productsToHydrate,
        hydratedProducts: products,
      });

      this._productHydrationState = {
        ...state,
        productsToHydrate: nextProductsToHydrate,
      };

      if (!nextProductsToHydrate.length) {
        finishHydration();
      } else {
        this._updateOverlayDOM();
      }
    };
    const timeoutId = setTimeout(finishHydration, PRODUCT_HYDRATION_TIMEOUT_MS);

    if (!Array.isArray(playerDelegate.eventSubscribers[INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT])) {
      playerDelegate.eventSubscribers[INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT] = [];
    }
    const { eventSubscribers: subscribers } = playerDelegate;
    this._productHydrationState = {
      productsToHydrate,
      resultHandler: productHydrationResultHandler,
      subscribers,
      timeoutId,
      resolve: resolveHydration,
    };
    subscribers[INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT].push(productHydrationResultHandler);

    // Invoke provided product hydration callback(s)
    for (const callback of productHydrationCallbacks) {
      try {
        Promise.resolve(callback({ products: productsToHydrate })).catch((err) => {
          console.warn('Failed while trying to hydrate products', err);
        });
      } catch (err) {
        console.warn('Failed while trying to hydrate products', err);
      }
    }

    return hydrationPromise;
  }

  /** Player control functions below */

  play() {
    this._shouldPlay = true;
    this._sendPlayerControlMessage('play');
  }

  pause() {
    this._shouldPlay = false;
    // Drop any pending loop restart — being paused outranks it.
    clearTimeout(this._loopRestartHandle);
    this._loopRestartHandle = undefined;
    this._sendPlayerControlMessage('pause');
  }

  load() {
    if (!this._setupStarted) {
      this._loadCalledBeforeSetup = true;
      this._setup();
      return;
    }

    if (this._attr.deferLoad) {
      this.removeAttribute('defer-load');
    }
    if (!this.iframe.src) {
      // Player iframe hasn't been loaded yet so we should do that first
      this._loadIframe({ forcePreload: true });
      return;
    }
    this._sendPlayerControlMessage('loadConditionally');
  }

  unmute() {
    this._sendPlayerControlMessage('unmute');
  }

  mute() {
    this._sendPlayerControlMessage('mute');
  }

  setCurrentTime(time) {
    if (typeof time !== 'number' || time < 0) throw new Error('setCurrentTime() expects a number');
    this._sendPlayerControlMessage('setCurrentTime', { time });
  }

  setScaleMode(scaleMode) {
    // scaleMode: 'aspectFit' | 'aspectFill' | 'fill'
    this._sendPlayerControlMessage('setScaleMode', scaleMode);
  }

  updateScaleMode() {
    this._attr.standalone ? this._updateScaleModeForStandalone() : this._updateScaleMode();
  }

  _updateScaleMode() {
    if (this._videoWidth > this._videoHeight) {
      // If the video is landscape and the window is portrait, we should fit the video
      if (this._attr.landscapeInPortrait === SCALE_MODE.FILL) {
        this.setScaleMode('aspectFill');
      } else {
        this.setScaleMode('aspectFit');
      }
    } else {
      this.setScaleMode('aspectFill');
    }
  }

  _updateScaleModeForStandalone() {
    const { width: containerWidth, height: containerHeight } = this.wrapper.getBoundingClientRect();
    const videoWidth = this._videoWidth;
    const videoHeight = this._videoHeight;

    const isLandscapeContainer = containerWidth > containerHeight;
    const isLandscapeVideo = videoWidth > videoHeight;

    if (this._isExpandedInPlaylist) {
      if (isLandscapeContainer) {
        this.setScaleMode('aspectFit');
      } else if (isLandscapeVideo) {
        // Portrait window, landscape video
        this._attr.landscapeInPortrait === SCALE_MODE.FILL
          ? this.setScaleMode('aspectFill')
          : this.setScaleMode('aspectFit');
      } else if (!isLandscapeVideo) {
        // Portrait window and portrait video
        // We fit or fill the video depending on the width difference
        // Not to leave ultra-thin black bars on the sides
        // Mimics functionality in player/src/v2/components/Player/Player.js
        const widthDiff = containerWidth - (containerHeight * ( videoWidth / videoHeight));
        const diffLimitToUse = Math.floor(containerWidth * 0.19);
        if (widthDiff > 0 && widthDiff < diffLimitToUse) {
          this.setScaleMode('aspectFill');
        } else {
          this.setScaleMode('aspectFit');
        }
      }
    } else {
      // Not expanded in playlist
      this.setScaleMode('aspectFill');
    }
  }

  setObjectPosition(objectPosition) {
    this._sendPlayerControlMessage('setObjectPosition', objectPosition);
  }

  _saveGlobalState({ muted }) {
    this._isMuted = muted;
  }

  _sendPlayerControlMessage(action, data) {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage({
      eventName: `widgets:${action}`,
      data,
    }, '*');
  }

  showProductViewInExpandedPlayer(productId) {
    this.embedInstance?.showProductView({ id: productId, stallUntilHydration: true });
  }

  showProductViewInBamProduct(productId, productData) {
    // TODO Check for currently active drawer and close it
    const bamProduct = document.createElement('bam-product');
    bamProduct.setAttribute('mode', this._attr.productAction);
    this.wrapper.appendChild(bamProduct);

    const videoSourceId = this._attr.videoId || this._attr.eventId;
    const detailEventIdKey = this._attr.videoId ? 'videoId' : 'eventId';
    const resolvedProductData = productData || this._getPreviewProductById(productId);
    const detail = {
      [detailEventIdKey]: videoSourceId,
      productId,
      productData: resolvedProductData,
      theme: this._theme,
      integrationConfig: this._getPlayerIntegrationConfig(),
      playerUrl: this._getPlayerUrl(),
      event: this._event,
      orgSettings: this._orgSettings,
      withCloseButton: true,
      eventHandlers: this.embedInstance?._playerEventHandlers || [],
      onClose: () => {
        bamProduct.remove();
      },
    };

    bamProduct.show(detail);

    // Let bamProduct connect to the same embedInstance as this player
    bamProduct._connectToEmbedInstance(this.embedInstance);
  }
}
